import {
  XStack,
  Text,
  Popover,
  Avatar,
  YStack,
  Separator,
  getTokens,
  useMedia,
  Stack,
  View,
  isWeb,
} from 'tamagui'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useUpNavigation } from '@hooks/useUpNavigation'
import { HeroIcon } from '@components/HeroIcon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { router } from 'expo-router'
import { useSignOut } from '@hooks/useUser'
import { useGetGeneralConfig } from '@hooks/useMembersConfig'
import { IBackRoute } from '@layouts/AppLayout'
import { ImageBackground, TouchableOpacity } from 'react-native'
import { useSession } from '@hooks/useSession'
import { HamburgerMenu } from '@components/HamburgerMenu'

interface IHeaderProps {
  backRoute?: IBackRoute
  isFixedHeader: boolean
  isVisible?: boolean
  logoUrl?: string | null
}

export function Header({
  backRoute,
  isFixedHeader,
  isVisible,
  logoUrl,
}: IHeaderProps) {
  const { signOutMutate } = useSignOut()
  const { isPreview } = useAdminPreview()
  const goUp = useUpNavigation()
  const { data: generalConfig } = useGetGeneralConfig()
  const faviconUrl = generalConfig?.faviconUrl
  const media = useMedia()
  const { user } = useSession()
  const [isOpen, setIsOpen] = useState(false)

  const setFavicon = useCallback(() => {
    if (!isWeb) return

    const favicons = document.querySelectorAll("link[rel*='icon']")

    if (faviconUrl) {
      if (favicons.length === 0) {
        const newLink = document.createElement('link')
        newLink.setAttribute('rel', 'icon')
        document.head.appendChild(newLink)
        const faviconWithCache = `${faviconUrl}?t=${new Date().getTime()}`
        newLink.setAttribute('href', faviconWithCache)
      } else {
        favicons.forEach((favicon) => {
          const faviconWithCache = `${faviconUrl}?t=${new Date().getTime()}`
          favicon.setAttribute('href', faviconWithCache)
        })
      }
    } else {
      favicons.forEach((favicon) => {
        favicon.parentNode?.removeChild(favicon)
      })

      const link = document.createElement('link')
      link.setAttribute('rel', 'icon')
      link.setAttribute('href', 'data:,')
      document.head.appendChild(link)

      setTimeout(() => {
        link.parentNode?.removeChild(link)
      }, 100)
    }
  }, [faviconUrl])

  useEffect(() => {
    setFavicon()
  }, [setFavicon])

  useEffect(() => {
    if (!isVisible) setIsOpen(false)
  }, [isVisible])

  const handleLogout = useCallback(() => {
    signOutMutate()
  }, [signOutMutate])

  const handleBack = useCallback(() => {
    if (backRoute?.back) {
      backRoute.back()
      return
    }
    goUp()
  }, [backRoute, goUp])

  const paddingHorizontal = useMemo(() => {
    const sizes = getTokens().size
    if (media.gtLg) return sizes['$spacing-xsl'].val
    if (media.gtMd) return sizes['$spacing-xl'].val

    return sizes[media.gtSm ? '$spacing-xxxs' : '$spacing-xs'].val
  }, [media.gtSm, media.gtMd, media.gtLg])

  const displaySchoolLogo = backRoute?.shouldDisplayLogo && logoUrl
  const containerJustifyContent =
    displaySchoolLogo || backRoute?.label ? 'space-between' : 'flex-end'
  const headerBackgroundColor = isVisible ? '$bg-chrome' : 'transparent'
  const popoverCrossAxis = isFixedHeader ? -65 : -50

  const handleProfilePress = useCallback(() => {
    window.location.href = `/users/${user?.id}/edit`
  }, [user?.id])

  const handleOrdersPress = useCallback(() => {
    window.location.href = '/orders'
  }, [])

  const handleMessagesPress = useCallback(() => {
    // No web quem decide entre members e hero_theme é o nginx (flag
    // members_page_messages), então precisa de full load: navegar pelo router
    // renderiza a mensageria do members mesmo com a flag desligada.
    if (isWeb) {
      window.location.href = '/channels'
      return
    }

    setIsOpen(false)
    router.push('/channels')
  }, [])

  const handleCertificatesPress = useCallback(() => {
    // No web o nginx precisa decidir entre members e hero_theme, então mantém o
    // full load. No nativo a tela de certificados vive no próprio app: navegar
    // pelo router evita abrir o browser externo com uma URL relativa inválida.
    if (isWeb) {
      window.location.href = '/certificates'
      return
    }

    setIsOpen(false)
    router.push('/certificates')
  }, [])

  const backButtonLabel = useMemo(() => {
    if (backRoute?.label && backRoute.label.length > 25) {
      return backRoute?.label.slice(0, 25) + '...'
    }
    return backRoute?.label
  }, [backRoute?.label])

  return (
    <Stack
      backgroundColor={headerBackgroundColor}
      position="absolute"
      width="100%"
      zIndex={1000}
    >
      <XStack
        width="100%"
        paddingVertical={16}
        backgroundColor="transparent"
        paddingHorizontal={paddingHorizontal}
        justifyContent={containerJustifyContent}
      >
        {backRoute && (
          <>
            {displaySchoolLogo ? (
              <View justifyContent="center" alignItems="center">
                <ImageBackground
                  testID="header-logo"
                  alt="logo"
                  resizeMode="contain"
                  source={{
                    uri: logoUrl,
                  }}
                  style={{
                    width: 124,
                    height: 40,
                  }}
                />
              </View>
            ) : backRoute.label ? (
              <TouchableOpacity
                testID="header-back-button"
                onPress={() => handleBack()}
                style={{
                  backgroundColor: 'transparent',
                  justifyContent: 'center',
                }}
              >
                <XStack
                  alignItems="center"
                  gap="$size.spacing-nano"
                  hoverStyle={{
                    opacity: 0.5,
                  }}
                >
                  <HeroIcon
                    icon={['fal', 'chevron-left']}
                    color="$accent"
                    size={16}
                  />
                  <Text
                    fontSize="$font-size-xxs"
                    ff="$mulishRegular"
                    color="$accent"
                    position="relative"
                    textDecorationLine="underline"
                  >
                    {backButtonLabel}
                  </Text>
                </XStack>
              </TouchableOpacity>
            ) : null}
          </>
        )}
        {isWeb && (
          <XStack $gtSm={{ display: 'flex' }}>
            <Popover
              allowFlip
              placement="bottom"
              offset={{
                mainAxis: 7,
                crossAxis: popoverCrossAxis,
              }}
              open={isOpen}
              onOpenChange={(open) => setIsOpen(open)}
            >
              <Popover.Trigger asChild>
                <View
                  paddingHorizontal="$spacing-inset-xxs"
                  paddingVertical="$spacing-inset-nano"
                  borderWidth={1}
                  cursor="pointer"
                  borderRadius="$border-radius-pill"
                  hoverStyle={{
                    borderColor: '$border-hover',
                    backgroundColor: 'transparent',
                  }}
                  focusStyle={{
                    borderColor: '$border-hover',
                    backgroundColor: 'transparent',
                  }}
                  pressStyle={{
                    borderColor: '$border-hover',
                    backgroundColor: 'transparent',
                    borderWidth: 1,
                  }}
                  borderColor="transparent"
                  onPress={() => setIsOpen(!isOpen)}
                  testID="header-menu-button"
                  role="button"
                  aria-label="Menu"
                >
                  <XStack gap={8} alignItems="center">
                    <Avatar
                      circular
                      size={24}
                      borderColor="$icon-secondary"
                      borderWidth={1}
                    >
                      <Avatar.Image src={user?.coverImageUrl} />
                      <Avatar.Fallback bc="$accent" />
                    </Avatar>
                    <HamburgerMenu
                      size={18}
                      strokeWidth={1}
                      animationDuration={500}
                      isOpen={isOpen}
                    />
                  </XStack>
                </View>
              </Popover.Trigger>
              <Popover.Content
                borderWidth={1}
                borderColor="$border-subtle"
                borderRadius={8}
                backgroundColor="$bg-surface"
                elevate
                padding={0}
              >
                <YStack>
                  <YStack paddingHorizontal={20} paddingVertical={20}>
                    <Text
                      fontSize="$font-size-xs"
                      ff="$mulishSemiBold"
                      color="$text-primary"
                    >
                      {user?.firstName} {user?.lastName}
                    </Text>
                    <Text
                      fontSize="$font-size-xs"
                      ff="$mulishRegular"
                      color="$text-primary"
                    >
                      {user?.email}
                    </Text>
                  </YStack>
                  <Separator borderColor="$border-subtle" />
                  <XStack
                    paddingHorizontal={20}
                    paddingVertical={12}
                    gap={8}
                    alignItems="center"
                    hoverStyle={{
                      cursor: 'pointer',
                      backgroundColor: '$bg-surface-raised',
                    }}
                    // Rotas de aluno: em preview o produtor não tem
                    // perfil/pedidos/mensagens/certificados, e o
                    // AdminPreviewRouter devolveria ele para /admin_view.
                    display={isPreview ? 'none' : 'flex'}
                    onPress={handleProfilePress}
                  >
                    <HeroIcon
                      icon={['fal', 'user']}
                      color="$icon-secondary"
                      size={18}
                    />
                    <Text
                      color="$text-primary"
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                    >
                      Meu perfil
                    </Text>
                  </XStack>
                  <XStack
                    paddingHorizontal={20}
                    paddingVertical={12}
                    gap={8}
                    alignItems="center"
                    hoverStyle={{
                      cursor: 'pointer',
                      backgroundColor: '$bg-surface-raised',
                    }}
                    // Rotas de aluno: em preview o produtor não tem
                    // perfil/pedidos/mensagens/certificados, e o
                    // AdminPreviewRouter devolveria ele para /admin_view.
                    display={isPreview ? 'none' : 'flex'}
                    onPress={handleOrdersPress}
                  >
                    <HeroIcon
                      icon={['fal', 'usd-circle']}
                      color="$icon-secondary"
                      size={18}
                    />
                    <Text
                      color="$text-primary"
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                    >
                      Meus pedidos
                    </Text>
                  </XStack>
                  <XStack
                    paddingHorizontal={20}
                    paddingVertical={12}
                    gap={8}
                    alignItems="center"
                    hoverStyle={{
                      cursor: 'pointer',
                      backgroundColor: '$bg-surface-raised',
                    }}
                    // Rotas de aluno: em preview o produtor não tem
                    // perfil/pedidos/mensagens/certificados, e o
                    // AdminPreviewRouter devolveria ele para /admin_view.
                    display={isPreview ? 'none' : 'flex'}
                    onPress={handleMessagesPress}
                  >
                    <HeroIcon
                      icon={['fal', 'envelope']}
                      color="$icon-secondary"
                      size={18}
                    />
                    <Text
                      color="$text-primary"
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                    >
                      Mensagens
                    </Text>
                  </XStack>
                  <XStack
                    paddingHorizontal={20}
                    paddingTop={12}
                    paddingBottom={16}
                    gap={8}
                    alignItems="center"
                    hoverStyle={{
                      cursor: 'pointer',
                      backgroundColor: '$bg-surface-raised',
                    }}
                    // Rotas de aluno: em preview o produtor não tem
                    // perfil/pedidos/mensagens/certificados, e o
                    // AdminPreviewRouter devolveria ele para /admin_view.
                    display={isPreview ? 'none' : 'flex'}
                    onPress={handleCertificatesPress}
                  >
                    <HeroIcon
                      icon={['fal', 'user-graduate']}
                      color="$icon-secondary"
                      size={18}
                    />
                    <Text
                      color="$text-primary"
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                    >
                      Certificados
                    </Text>
                  </XStack>
                  <Separator borderColor="$border-subtle" />
                  <XStack
                    paddingHorizontal={20}
                    paddingTop={12}
                    paddingBottom={20}
                    gap={8}
                    alignItems="center"
                    hoverStyle={{
                      cursor: 'pointer',
                      backgroundColor: '$bg-surface-raised',
                    }}
                    onPress={() => handleLogout()}
                  >
                    <HeroIcon
                      icon={['fal', 'door-open']}
                      color="$icon-secondary"
                      size={18}
                    />
                    <Text
                      color="$text-primary"
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                    >
                      Sair
                    </Text>
                  </XStack>
                </YStack>
              </Popover.Content>
            </Popover>
          </XStack>
        )}
      </XStack>
    </Stack>
  )
}
